<template>
  <AdminLayout>
    <div class="flex flex-col gap-4">
      <!-- 搜索和操作条 -->
      <div class="flex justify-between items-center">
        <div class="flex gap-2">
          <el-input
            v-model="searchQuery"
            :placeholder="t('common.pleaseInput') + t('admin.aigc.tag.fields.name')"
            class="!w-[240px]"
            clearable
            @clear="handleSearch"
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" @click="handleSearch">{{ t('common.search') }}</el-button>
        </div>
        <el-button type="primary" @click="handleAdd">
          <template #icon><el-icon><Plus /></el-icon></template>
          {{ t('admin.aigc.tag.add') }}
        </el-button>
      </div>

      <!-- 数据表格 -->
      <el-table :data="rows" v-loading="loading" border style="width: 100%">
        <el-table-column prop="id" :label="t('common.id')" width="80" align="center" />
        <el-table-column prop="name" :label="t('admin.aigc.tag.fields.name')" min-width="150" />
        <el-table-column prop="taggingsCount" :label="t('admin.aigc.tag.fields.taggingsCount')" width="120" align="center" />
        <el-table-column prop="createdAt" :label="t('common.createdAt')" width="180">
          <template #default="{ row }">
            {{ dayjs(row.createdAt).format('YYYY-MM-DD HH:mm:ss') }}
          </template>
        </el-table-column>
        <el-table-column :label="t('common.operation')" width="180" fixed="right" align="center">
          <template #default="{ row }">
            <el-button link type="primary" @click="handleEdit(row)">
              {{ t('common.edit') }}
            </el-button>
            <el-popconfirm
              :title="t('common.deleteConfirm')"
              @confirm="handleDelete(row)"
            >
              <template #reference>
                <el-button link type="danger">
                  {{ t('common.delete') }}
                </el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="flex justify-end">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 编辑表单对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? t('admin.aigc.tag.edit') : t('admin.aigc.tag.add')"
      width="400px"
      append-to-body
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="80px"
        @submit.prevent="handleSubmit"
      >
        <el-form-item :label="t('admin.aigc.tag.fields.name')" prop="name">
          <el-input v-model="form.name" :placeholder="t('admin.aigc.tag.namePlaceholder')" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">{{ t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="submitLoading" @click="handleSubmit">
            {{ t('common.confirm') }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </AdminLayout>
</template>

<script setup>
import { Search, Plus } from '@element-plus/icons-vue'
import AdminLayout from '@/layouts/Admin.vue'
import { router } from '@inertiajs/vue3'
import dayjs from 'dayjs'
import axios from 'axios'
import { ElMessage } from 'element-plus'

const props = defineProps({
  rows: { type: Array, default: () => [] },
  pagy: { type: Object, default: () => ({}) }
})

const { t } = useI18n()
const loading = ref(false)
const searchQuery = ref('')
const dialogVisible = ref(false)
const isEdit = ref(false)
const submitLoading = ref(false)
const formRef = ref(null)

const pagination = reactive({
  page: props.pagy.page || 1,
  size: props.pagy.limit || 20,
  total: props.pagy.count || 0
})

const form = reactive({
  id: null,
  name: ''
})

const rules = {
  name: [
    { required: true, message: t('admin.aigc.tag.nameRequired'), trigger: 'blur' },
    { max: 50, message: t('common.too_long', { count: 50 }), trigger: 'blur' }
  ]
}

const handleSearch = () => {
  router.get(window.location.pathname, { q: searchQuery.value, size: pagination.size }, {
    preserveState: true,
    preserveScroll: true
  })
}

const handlePageChange = (page) => {
  router.get(window.location.pathname, { page, q: searchQuery.value, size: pagination.size }, {
    preserveState: true,
    preserveScroll: true
  })
}

const handleSizeChange = (size) => {
  router.get(window.location.pathname, { size, q: searchQuery.value }, {
    preserveState: true,
    preserveScroll: true
  })
}

const handleAdd = () => {
  isEdit.value = false
  form.id = null
  form.name = ''
  dialogVisible.value = true
  nextTick(() => {
    formRef.value?.clearValidate()
  })
}

const handleEdit = (row) => {
  isEdit.value = true
  form.id = row.id
  form.name = row.name
  dialogVisible.value = true
  nextTick(() => {
    formRef.value?.clearValidate()
  })
}

const handleDelete = (row) => {
  router.delete(route('admin_aigc_tag_path', { id: row.id }), {
    onSuccess: () => {
      ElMessage.success(t('flash.actions.destroy.success'))
    }
  })
}

const handleSubmit = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      submitLoading.value = true
      router.post(routes.upsert_admin_aigc_tags(), form, {
        onSuccess: () => {
          ElMessage.success(isEdit.value ? t('flash.actions.update.success') : t('flash.actions.create.success'))
          dialogVisible.value = false
        },
        onError: (errors) => {
          ElMessage.error(Object.values(errors).join(', '))
        },
        onFinish: () => {
          submitLoading.value = false
        }
      })
    }
  })
}
</script>
